import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { OverlayPanel } from 'primereact/overlaypanel';
import useDisablePageScroll from '../../hooks/use-disable-page-scroll';
import adjustOverlayPosition from '../IntegrationErrorsBadge/adjust-overlay-position';

export interface BadgeOverlayRef {
  toggleOverlay: (event: React.MouseEvent<HTMLButtonElement>) => void;
  hideOverlay: () => void;
}

interface BadgeOverlayProps {
  onOverlayHide?: () => void;
  children: React.ReactNode;
}

const BadgeOverlay = forwardRef<BadgeOverlayRef, BadgeOverlayProps>(
  ({ onOverlayHide, children }, ref) => {
    const overlayPanelRef = useRef<OverlayPanel>(null);
    const [isVisible, setIsVisible] = useState(false);

    useDisablePageScroll(isVisible);

    useImperativeHandle(
      ref,
      () => ({
        toggleOverlay: (event: React.MouseEvent<HTMLButtonElement>) => {
          overlayPanelRef.current?.toggle(event);
        },
        hideOverlay: () => {
          overlayPanelRef.current?.hide();
        },
      }),
      [],
    );

    const handleShow = useImmutableCallback(() => {
      setIsVisible(true);
      const overlayElement = overlayPanelRef.current?.getElement();

      if (overlayElement) {
        adjustOverlayPosition(overlayElement);
      }
    });

    const handleHide = useImmutableCallback(() => {
      setIsVisible(false);
      onOverlayHide?.();
    });

    return (
      <OverlayPanel
        ref={overlayPanelRef}
        className="integration-errors-overlay"
        dismissable
        onShow={handleShow}
        onHide={handleHide}
      >
        {children}
      </OverlayPanel>
    );
  },
);

BadgeOverlay.displayName = 'BadgeOverlay';

export default BadgeOverlay;
